"use client";

import { useLocale, useTranslations } from "next-intl";
import { useState } from "react";
import { Link, usePathname } from "@/i18n/navigation";
import { routing } from "@/i18n/routing";

const NAV_IDS = [
  "about",
  "plans",
  "amenities",
  "location",
  "investment",
  "faq",
] as const;

const LANG_LABELS: Record<string, { code: string; name: string }> = {
  en: { code: "EN", name: "English" },
  ru: { code: "RU", name: "Русский" },
  th: { code: "TH", name: "ไทย" },
  zh: { code: "ZH", name: "中文" },
};

export function Header() {
  const t = useTranslations();
  const locale = useLocale();
  const pathname = usePathname();
  const nav = t.raw("nav") as string[];
  const [menuOpen, setMenuOpen] = useState(false);
  const [langsOpen, setLangsOpen] = useState(false);

  // nav[] has 7 items (About, Layouts, Facilities, Location, Gallery, Investment, FAQ);
  // Gallery has no standalone section on this page, so it's excluded from the rendered links.
  const navItems = NAV_IDS.map((id, i) => ({
    id,
    label: i < 4 ? nav[i] : nav[i + 1],
  }));

  return (
    <header className="sop-header fixed inset-x-0 top-0 z-[60] border-b border-ink/[.09] bg-bg/[.93] backdrop-blur-md">
      <div className="mx-auto flex h-[72px] max-w-[1280px] items-center gap-[clamp(14px,1.6vw,26px)] px-[clamp(20px,4vw,40px)]">
        <Link
          href="#top"
          locale={locale}
          className="flex flex-none items-baseline gap-2"
        >
          <span className="font-display text-[17px] font-bold tracking-[-0.01em] text-ink">
            SO ORIGIN
          </span>
          <span className="text-[11px] tracking-[.16em] text-muted uppercase">
            Pattaya
          </span>
        </Link>

        <nav
          aria-label="Sections"
          className="sop-scroll-strip ml-auto hidden min-w-0 flex-nowrap items-center gap-[clamp(14px,1.6vw,26px)] overflow-x-auto min-[1180px]:flex"
        >
          {navItems.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className="whitespace-nowrap border-b border-transparent py-1.5 text-[13.5px] font-medium text-muted transition-colors hover:border-accent hover:text-ink"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="relative hidden flex-none items-center gap-0.5 rounded-[3px] border border-ink/[.12] p-[3px] min-[1180px]:flex">
          <button
            type="button"
            onClick={() => setLangsOpen((v) => !v)}
            aria-haspopup="true"
            aria-expanded={langsOpen}
            // Доступное имя обязано содержать видимый текст кнопки (код локали),
            // иначе голосовое управление «нажми EN» не находит элемент.
            aria-label={`Language: ${LANG_LABELS[locale]?.code ?? locale.toUpperCase()}`}
            className="flex min-w-[34px] items-center gap-[7px] rounded-[2px] border-0 bg-transparent px-[7px] py-[5px] text-[11.5px] font-semibold tracking-[.06em] text-ink"
          >
            <span>{LANG_LABELS[locale]?.code ?? locale.toUpperCase()}</span>
            <span
              aria-hidden
              className="h-1.5 w-1.5 border-b-[1.5px] border-r-[1.5px] border-muted transition-transform"
              style={{ transform: langsOpen ? "rotate(-135deg)" : "rotate(45deg)" }}
            />
          </button>
          {langsOpen ? (
            <div
              role="menu"
              className="absolute right-0 top-[calc(100%+8px)] z-[70] grid min-w-[150px] gap-px rounded-[3px] border border-ink/[.12] bg-white p-[5px] shadow-[0_18px_40px_-22px_rgba(26,29,33,.45)]"
            >
              {routing.locales.map((l) => {
                const active = l === locale;
                return (
                  <Link
                    key={l}
                    href={pathname}
                    locale={l}
                    role="menuitem"
                    aria-current={active ? "true" : undefined}
                    onClick={() => setLangsOpen(false)}
                    className="flex items-center justify-between gap-3.5 rounded-[2px] px-[11px] py-[9px] text-left text-[13px] font-medium transition-colors"
                    style={{
                      background: active ? "var(--color-accent)" : "transparent",
                      color: active ? "#fff" : "#1A1D21",
                    }}
                  >
                    <span>{LANG_LABELS[l]?.name ?? l}</span>
                    <span className="text-[10.5px] font-semibold tracking-[.08em] opacity-65">
                      {LANG_LABELS[l]?.code ?? l.toUpperCase()}
                    </span>
                  </Link>
                );
              })}
            </div>
          ) : null}
        </div>

        <a
          href="#contact"
          className="ml-auto flex h-8 flex-none items-center whitespace-nowrap rounded-[3px] bg-accent px-[18px] text-[13px] font-medium text-white transition-colors hover:bg-ink min-[1180px]:ml-[calc(8px-clamp(14px,1.6vw,26px))]"
        >
          {t("contact")}
        </a>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Menu"
          aria-expanded={menuOpen}
          className="flex h-8 w-8 flex-none items-center justify-center rounded-[3px] border border-ink/[.14] bg-none min-[1180px]:hidden"
        >
          <span aria-hidden className="relative block h-3 w-[18px]">
            <span className="absolute left-0 top-0 h-[1.5px] w-[18px] bg-ink" />
            <span className="absolute left-0 top-[5px] h-[1.5px] w-[18px] bg-ink" />
            <span className="absolute left-0 top-[10px] h-[1.5px] w-[18px] bg-ink" />
          </span>
        </button>
      </div>

      {menuOpen ? (
        <div className="max-h-[calc(100vh-72px)] overflow-y-auto border-t border-ink/[.09] bg-bg px-[clamp(20px,4vw,40px)] pb-[26px] pt-[18px]">
          <nav aria-label="Sections" className="grid gap-0.5">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={() => setMenuOpen(false)}
                className="border-b border-ink/[.08] py-[13px] text-[16px] font-medium text-ink"
              >
                {item.label}
              </a>
            ))}
          </nav>
          <div
            role="group"
            aria-label="Language"
            className="mt-[22px] flex flex-wrap gap-2"
          >
            {routing.locales.map((l) => {
              const active = l === locale;
              return (
                <Link
                  key={l}
                  href={pathname}
                  locale={l}
                  aria-pressed={active}
                  onClick={() => setMenuOpen(false)}
                  className="min-w-[54px] rounded-[3px] border border-ink/[.14] px-[14px] py-[11px] text-center text-[12.5px] font-semibold tracking-[.08em]"
                  style={{
                    background: active ? "var(--color-accent)" : "transparent",
                    color: active ? "#fff" : "#5A626B",
                  }}
                >
                  {LANG_LABELS[l]?.code ?? l.toUpperCase()}
                </Link>
              );
            })}
          </div>
        </div>
      ) : null}
    </header>
  );
}
